import React, { useState } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { AlertTriangle } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';

interface CancelPaymentModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  receiptNumber: string;
  isLoading?: boolean;
}

export function CancelPaymentModal({
  isOpen,
  onClose,
  onConfirm,
  receiptNumber,
  isLoading = false
}: CancelPaymentModalProps) {
  const [inputValue, setInputValue] = useState('');
  const [error, setError] = useState('');

  const handleConfirm = () => {
    if (inputValue !== receiptNumber) {
      setError('Receipt number does not match. Please enter the correct receipt number.');
      return;
    }
    setError('');
    onConfirm();
  };

  const handleClose = () => {
    setInputValue('');
    setError('');
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-destructive">
            <AlertTriangle className="h-5 w-5" />
            Cancel Payment
          </DialogTitle>
          <DialogDescription>
            This action cannot be undone. All accounting entries will be reversed.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <Alert className="border-destructive/50 bg-destructive/10">
            <AlertTriangle className="h-4 w-4 text-destructive" />
            <AlertDescription className="text-sm">
              <strong>Warning:</strong> Cancelling this payment will:
              <ul className="list-disc list-inside mt-2 space-y-1">
                <li>Reverse all accounting entries</li>
                <li>Update the invoice status to unpaid</li>
                <li>Remove this payment from your records</li>
              </ul>
            </AlertDescription>
          </Alert>

          <div className="space-y-2">
            <Label htmlFor="receipt-confirm">
              To confirm cancellation, please type the receipt number: <strong>{receiptNumber}</strong>
            </Label>
            <Input
              id="receipt-confirm"
              value={inputValue}
              onChange={(e) => {
                setInputValue(e.target.value);
                setError('');
              }}
              placeholder="Enter receipt number"
              className={error ? 'border-destructive' : ''}
              autoComplete="off"
            />
            {error && (
              <p className="text-sm text-destructive">{error}</p>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleClose} disabled={isLoading}>
            Keep Payment
          </Button>
          <Button 
            variant="destructive" 
            onClick={handleConfirm}
            disabled={isLoading || !inputValue}
          >
            {isLoading ? 'Cancelling...' : 'Cancel Payment'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}